import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const VerifyEmail: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const { verifyEmail, resendVerificationCode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get email from location state
  useEffect(() => {
    const stateEmail = (location.state as { email?: string })?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    }
  }, [location.state]);
  
  // Handle countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);
    
    try {
      await verifyEmail({ email, code: verificationCode });
      setSuccess('Email verified successfully!');
      // Redirect to nomination categories after successful verification
      setTimeout(() => navigate('/nomination-categories'), 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data 
        ? String(err.response.data.message)
        : 'Verification failed. Please check your code and try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendCode = async () => {
    setError(null);
    setSuccess(null);
    setIsResending(true);
    
    try {
      await resendVerificationCode(email);
      setSuccess('Verification code has been resent to your email');
      setCountdown(60); // Start 60 second countdown
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data 
        ? String(err.response.data.message)
        : 'Failed to resend verification code. Please try again.';
      setError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };
  
  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Verify Your Email</h1>
              <p className="mt-2 text-gray-600">
                We've sent a verification code to<br />
                <span className="font-medium">{email || 'your email address'}</span>
              </p>
            </div>
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
                {success}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  id="verificationCode"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--deep-blue)] focus:border-transparent text-center text-xl tracking-wider"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !verificationCode}
                className="w-full py-3 px-4 bg-[var(--deep-blue)] text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Verifying...
                  </div>
                ) : 'Verify Email'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-gray-600 mb-2">Didn't receive a code?</p>
              <button
                onClick={handleResendCode}
                disabled={isResending || countdown > 0}
                className="text-[var(--deep-blue)] font-medium hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend verification code'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerifyEmail;
